// EnchantedGiftEmporiumLayout.js - Fixed version with working hero images

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component - FIXED
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    // Check multiple sources for the value
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-pink-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-100 dark:border-pink-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("fullcontent");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function EnchantedGiftEmporiumLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#EC4899"; // Pink
  const secondary = T.secondaryColor || "#F472B6"; // Light Pink
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isFloating, setIsFloating] = useState(true);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Hero Images - DIRECTLY FROM CONFIG, NOT FROM storeTheme
  const giftHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=1400&q=80",
    },
    {
      field: "enchantedHeroImage2",
      src: "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=1400&q=80",
    },
    {
      field: "enchantedHeroImage3",
      src: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1400&q=80",
    },
    {
      field: "enchantedHeroImage4",
      src: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=1400&q=80",
    },
  ];

  // Gift Products - Cute & Lovely
  const giftProducts = [
    // Personalized Gifts
    {
      id: 1,
      nameKey: "product1_name",
      name: "Personalized Name Necklace",
      priceKey: "product1_price",
      price: 1499,
      originalPriceKey: "product1_original_price",
      originalPrice: 1999,
      image: "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&q=80",
      categoryKey: "product1_category",
      category: "Personalized",
      subCategory: "Jewelry",
      descriptionKey: "product1_description",
      description: "Beautiful 14k gold-plated necklace with custom name engraving. Perfect for loved ones.",
      badge: "Best Seller",
      badgeColor: "#EC4899",
      quantity: "1 piece",
      brand: "EnchantedGifts",
      occasion: "Anniversary, Birthday",
      material: "Gold Plated"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Custom Photo Frame Set",
      priceKey: "product2_price",
      price: 899,
      originalPriceKey: "product2_original_price",
      originalPrice: 1299,
      image: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=400&q=80",
      categoryKey: "product2_category",
      category: "Personalized",
      subCategory: "Photo Frames",
      descriptionKey: "product2_description",
      description: "Elegant wooden photo frames with custom engraving. Set of 2.",
      badge: "Popular",
      badgeColor: "#F472B6",
      quantity: "Set of 2",
      brand: "EnchantedGifts",
      occasion: "Wedding, Anniversary",
      material: "Wood"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Customized Love Letters Set",
      priceKey: "product3_price",
      price: 649,
      originalPriceKey: "product3_original_price",
      originalPrice: 899,
      image: "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=400&q=80",
      categoryKey: "product3_category",
      category: "Personalized",
      subCategory: "Stationery",
      descriptionKey: "product3_description",
      description: "Beautiful handcrafted love letters set with custom messages.",
      badge: "Romantic",
      badgeColor: "#EC4899",
      quantity: "Set of 5",
      brand: "EnchantedGifts",
      occasion: "Valentine's Day",
      material: "Handmade Paper"
    },
    
    // Gift Hampers
    {
      id: 4,
      nameKey: "product4_name",
      name: "Luxury Gift Hamper",
      priceKey: "product4_price",
      price: 2499,
      originalPriceKey: "product4_original_price",
      originalPrice: 3499,
      image: "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&q=80",
      categoryKey: "product4_category",
      category: "Hampers",
      subCategory: "Luxury",
      descriptionKey: "product4_description",
      description: "Premium gift hamper with chocolates, wine, and luxury items.",
      badge: "Premium",
      badgeColor: "#F472B6",
      quantity: "1 hamper",
      brand: "EnchantedGifts",
      occasion: "Corporate, Anniversary",
      items: "10+ items"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Chocolate Gift Box",
      priceKey: "product5_price",
      price: 999,
      originalPriceKey: "product5_original_price",
      originalPrice: 1499,
      image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400&q=80",
      categoryKey: "product5_category",
      category: "Hampers",
      subCategory: "Chocolates",
      descriptionKey: "product5_description",
      description: "Assorted premium chocolates in a beautiful gift box.",
      badge: "Sweet",
      badgeColor: "#EC4899",
      quantity: "24 pieces",
      brand: "EnchantedGifts",
      occasion: "Birthday, Valentine's",
      items: "Premium Chocolates"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Spa & Wellness Hamper",
      priceKey: "product6_price",
      price: 1799,
      originalPriceKey: "product6_original_price",
      originalPrice: 2499,
      image: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=400&q=80",
      categoryKey: "product6_category",
      category: "Hampers",
      subCategory: "Wellness",
      descriptionKey: "product6_description",
      description: "Relaxing spa hamper with essential oils, candles, and wellness products.",
      badge: "Relaxing",
      badgeColor: "#F472B6",
      quantity: "1 hamper",
      brand: "EnchantedGifts",
      occasion: "Mother's Day",
      items: "8+ items"
    },
    
    // Flowers & Decor
    {
      id: 7,
      nameKey: "product7_name",
      name: "Eternal Rose Box",
      priceKey: "product7_price",
      price: 1299,
      originalPriceKey: "product7_original_price",
      originalPrice: 1799,
      image: "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=400&q=80",
      categoryKey: "product7_category",
      category: "Flowers",
      subCategory: "Preserved Roses",
      descriptionKey: "product7_description",
      description: "Beautiful preserved rose in a glass box. Lasts forever.",
      badge: "Eternal Love",
      badgeColor: "#EC4899",
      quantity: "1 rose",
      brand: "EnchantedGifts",
      occasion: "Anniversary",
      material: "Preserved Rose"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Dreamcatcher Set",
      priceKey: "product8_price",
      price: 799,
      originalPriceKey: "product8_original_price",
      originalPrice: 1099,
      image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400&q=80",
      categoryKey: "product8_category",
      category: "Decor",
      subCategory: "Dreamcatchers",
      descriptionKey: "product8_description",
      description: "Handmade dreamcatcher with crystals and feathers.",
      badge: "Bohemian",
      badgeColor: "#F472B6",
      quantity: "1 piece",
      brand: "EnchantedGifts",
      occasion: "Housewarming",
      material: "Macrame"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "Fairy Light Jar",
      priceKey: "product9_price",
      price: 549,
      originalPriceKey: "product9_original_price",
      originalPrice: 799,
      image: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=400&q=80",
      categoryKey: "product9_category",
      category: "Decor",
      subCategory: "Lighting",
      descriptionKey: "product9_description",
      description: "Enchanting fairy light jar with remote control. Perfect gift.",
      badge: "Magical",
      badgeColor: "#EC4899",
      quantity: "1 jar",
      brand: "EnchantedGifts",
      occasion: "Birthday",
      material: "Glass, LED"
    }
  ];

  // Gift Categories
  const giftCategories = [
    { key: "all", icon: "🎁", label: "All Gifts", color: "#EC4899" },
    { key: "personalized", icon: "💝", label: "Personalized", color: "#F472B6" },
    { key: "hampers", icon: "🧺", label: "Gift Hampers", color: "#EC4899" },
    { key: "flowers", icon: "🌹", label: "Flowers", color: "#F472B6" },
    { key: "decor", icon: "✨", label: "Home Decor", color: "#EC4899" },
  ];

  // Occasion Categories with detailed descriptions
  const occasionCategories = [
    { 
      icon: "❤️", 
      label: "Valentine's Day",
      description: "Express your love with romantic gifts that speak from the heart. From eternal roses to personalized jewelry.",
      color: "#EC4899"
    },
    { 
      icon: "🎂", 
      label: "Birthday", 
      description: "Make their special day unforgettable with curated gifts that show how much you care.",
      color: "#F472B6"
    },
    { 
      icon: "💍", 
      label: "Anniversary", 
      description: "Celebrate your journey together with timeless gifts that capture your love story.",
      color: "#EC4899"
    },
    { 
      icon: "👶", 
      label: "Baby Shower", 
      description: "Welcome the little one with adorable gifts filled with love and blessings.",
      color: "#F472B6"
    },
    { 
      icon: "🎄", 
      label: "Christmas", 
      description: "Spread holiday cheer with magical gifts that capture the spirit of Christmas.",
      color: "#EC4899"
    },
    { 
      icon: "🌺", 
      label: "Mother's Day", 
      description: "Show your appreciation with heartfelt gifts that celebrate the amazing women in your life.",
      color: "#F472B6"
    },
  ];

  // Gift Stories with full content for popup
  const giftStories = [
    {
      id: 1,
      titleKey: "story1_title",
      title: "A Love Story Written in Gold",
      descKey: "story1_desc",
      desc: "How a personalized necklace brought two hearts closer across miles",
      storyContentKey: "story1_content",
      fullContent: "When Sarah ordered a custom name necklace for her long-distance boyfriend, she never imagined it would become a symbol of their unbreakable bond. The necklace, engraved with both their names, traveled across continents and became a daily reminder of their love. Every time he wore it, he felt her presence, and the distance seemed to disappear. Today, they're married and that necklace remains their most treasured possession, a testament to the power of thoughtful, personalized gifts.",
      authorKey: "story1_author",
      author: "Emma Thompson",
      dateKey: "story1_date",
      date: "February 14, 2025",
      imageField: "enchantedStoryImage1",
      image: "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=600&q=80",
    },
    {
      id: 2,
      titleKey: "story2_title",
      title: "A Surprise That Changed Everything",
      descKey: "story2_desc",
      desc: "The unexpected gift that brought a family back together",
      storyContentKey: "story2_content",
      fullContent: "Mark had been estranged from his father for years. On his father's 60th birthday, he decided to send a custom photo frame with old family pictures and a heartfelt letter. The gift arrived unexpectedly, and it opened a door that had been closed for too long. The photo frame now sits proudly on his father's desk, serving as a daily reminder that love and forgiveness can heal even the deepest wounds.",
      authorKey: "story2_author",
      author: "Michael Rodriguez",
      dateKey: "story2_date",
      date: "January 20, 2025",
      imageField: "enchantedStoryImage2",
      image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&q=80",
    },
    {
      id: 3,
      titleKey: "story3_title",
      title: "The Gift That Keeps on Giving",
      descKey: "story3_desc",
      desc: "How a simple dreamcatcher brought peace to a troubled soul",
      storyContentKey: "story3_content",
      fullContent: "Sophia was going through a difficult time, struggling with anxiety and sleepless nights. Her best friend gifted her a handmade dreamcatcher, hoping to bring her some peace. That dreamcatcher became her sanctuary. She would sit beneath it, watching the crystals catch the light, and find moments of calm. Today, Sophia is a mindfulness coach, and she credits that simple gift with helping her discover her path to inner peace.",
      authorKey: "story3_author",
      author: "Priya Sharma",
      dateKey: "story3_date",
      date: "December 5, 2024",
      imageField: "enchantedStoryImage3",
      image: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=600&q=80",
    }
  ];

  // Navigation items - Simplified for gift store
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_gifts", label: "GIFTS", page: "gifts" },
    { key: "nav_occasions", label: "OCCASIONS", page: "occasions" },
    { key: "nav_stories", label: "STORIES", page: "stories" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🎁 FIND THE PERFECT GIFT",
    "✦ FREE GIFT WRAPPING",
    "💝 PERSONALIZED GIFTS AVAILABLE",
    "✦ FREE DELIVERY ON ORDERS ₹500+"
  ];

  // Floating hearts and stars
  const floatingElements = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 8 + Math.random() * 16,
    delay: Math.random() * 6,
    duration: 6 + Math.random() * 8,
    opacity: 0.05 + Math.random() * 0.1,
    type: Math.random() > 0.5 ? 'heart' : 'star',
    color: ['#EC4899', '#F472B6', '#FB7185', '#F59E0B', '#8B5CF6'][Math.floor(Math.random() * 5)]
  }));

  // Auto-rotate hero images
  useEffect(() => {
    if (p.previewEditMode) {
      return undefined;
    }
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % giftHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [giftHeroImageConfigs.length, p.previewEditMode]);

  useEffect(() => {
    if (!p.previewEditMode || !p.activeEditField) {
      return;
    }

    const index = giftHeroImageConfigs.findIndex((image) => image.field === p.activeEditField);
    if (index >= 0 && index !== activeSlide) {
      setActiveSlide(index);
    }
  }, [activeSlide, giftHeroImageConfigs, p.activeEditField, p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openStoryModal = useCallback((story, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedStory(story);
  }, [p.previewEditMode, p.activeEditField]);

  const closeStoryModal = useCallback(() => {
    setSelectedStory(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedStory(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || giftHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = giftHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, giftHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "ENCHANTED GIFTS",
    title: p.storeTheme?.heroTitle || "Find the Perfect Gift",
    subtitle: p.storeTheme?.heroSubtitle || "Unique, personalized gifts for every occasion",
    primaryCta: p.storeTheme?.primaryCtaText || "Explore Gifts",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Stories",
  };

  const editableGiftProducts = giftProducts.map((product) => {
    const imageField = `enchantedProductImage${product.id}`;
    return {
      ...product,
      imageField,
      image: p.storeTheme?.[imageField] || product.image,
    };
  });

  const editableGiftStories = giftStories.map((story) => {
    const imageField = story.imageField || `enchantedStoryImage${story.id}`;
    return {
      ...story,
      imageField,
      image: p.storeTheme?.[imageField] || story.image,
    };
  });

  // Get filtered items
  const getFilteredItems = (category) => {
    if (category === "all") return editableGiftProducts;
    const map = {
      "personalized": "Personalized",
      "hampers": "Hampers",
      "flowers": "Flowers",
      "decor": "Decor",
    };
    return editableGiftProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -30, y: -15, rotate: -3 },
      { x: 30, y: -15, rotate: 3 },
      { x: -30, y: 15, rotate: -2 },
      { x: 30, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-pink-50/50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'DM Sans', sans-serif" }}>
      
      {/* Floating Hearts & Stars */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className={`absolute ${element.type === 'heart' ? 'heart-float' : 'star-float'}`}
            style={{
              left: `${element.left}%`,
              top: `${element.top}%`,
              width: `${element.size}px`,
              height: `${element.size}px`,
              color: element.color,
              fontSize: `${element.size}px`,
              opacity: element.opacity,
              animationDelay: `${element.delay}s`,
              animationDuration: `${element.duration}s`,
            }}
          >
            {element.type === 'heart' ? '♥' : '✦'}
          </div>
        ))}
      </div>
      
      {/* Top Bar - Cute Style */}
      <div className="relative bg-gradient-to-r from-pink-600 to-rose-500 text-pink-100 py-2.5 overflow-hidden border-b-2 border-pink-400/30">
        <div className="flex items-center justify-center gap-6 whitespace-nowrap animate-marquee-slow">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-5 text-xs tracking-wider">
              <span className="mr-2">✨</span>
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Dreamy Style */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-white/95 backdrop-blur-xl shadow-lg py-2 border-b border-pink-100" : "bg-white/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center text-xl border-2 border-pink-300/50 shadow-lg shadow-pink-400/20">
              <span className="text-white">🎁</span>
            </div>
            <span className="font-bold text-xl tracking-tight text-pink-700">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Enchanted Gifts"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-pink-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-pink-600" 
                    : "text-pink-600/70 hover:text-pink-800"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-pink-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-pink-50">
                <FaHeart className="text-lg text-pink-500/70 hover:text-pink-600 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={false} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-pink-100">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => {
                  navigateTo(item.page, e);
                  setMobileMenuOpen(false);
                }}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-pink-50 text-pink-600" 
                    : "text-pink-600/70 hover:text-pink-800"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - DIRECTLY USING CONFIG IMAGES */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {giftHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Gift ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-pink-900/70 via-pink-900/40 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-pink-50/80 via-transparent to-transparent z-20 pointer-events-none" />
              
              <div className="absolute bottom-0 left-0 right-0 z-20">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                  <path d="M0 60 C 360 0, 720 120, 1080 60 C 1260 30, 1380 90, 1440 60 L 1440 120 L 0 120 Z" fill="#FDF2F8" opacity="0.95" />
                </svg>
              </div>
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                {p.previewEditMode && (
                  <div className="mb-5 inline-flex flex-wrap items-center gap-2 rounded-2xl border border-pink-300/40 bg-pink-900/35 px-3 py-2 backdrop-blur-sm">
                    {giftHeroImageConfigs.map((image, index) => (
                      <button
                        key={image.field}
                        type="button"
                        onClick={(event) => handleEditHeroImage(event, image.field)}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                          index === activeSlide ? "bg-pink-500 text-white" : "bg-white/10 text-pink-100 hover:bg-white/20"
                        }`}
                      >
                        Hero {index + 1}
                      </button>
                    ))}
                  </div>
                )}

                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-pink-800/40 backdrop-blur-md rounded-full border border-pink-400/30">
                    <span className="w-2 h-2 rounded-full bg-pink-400 animate-ping" />
                    <span className="text-pink-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.12}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-pink-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("gifts", e)} 
                    className="group px-8 py-4 rounded-full bg-pink-500 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="gifts"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("stories", e)} 
                    className="px-8 py-4 rounded-full border-2 border-pink-400/50 text-pink-200 font-semibold tracking-wide transition-all duration-300 hover:bg-pink-600/30 hover:border-pink-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="stories"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-pink-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE GIFTS"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-pink-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-pink-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge - Cute */}
          <div className="relative z-10 -mt-6 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-pink-200">
              <span className="text-pink-500 text-sm">✨</span>
              <span className="text-pink-700 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="CURATED WITH LOVE ❤️"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-pink-500 text-sm">✨</span>
            </div>
          </div>
          
          {/* Occasion Cards - Circular Layout with Details */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="text-center mb-8">
              <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="occasions_badge" 
                  value="Shop by Occasion"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="occasions"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-pink-700 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="occasions_title" 
                  value="Find Gifts for Every Moment"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="occasions"
                />
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              {occasionCategories.map((occasion, idx) => (
                <div 
                  key={idx} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={() => navigateTo("occasions")}
                >
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex flex-col items-center justify-center border-2 border-pink-200 group-hover:border-pink-400 transition-all duration-500 group-hover:scale-110 group-hover:shadow-xl shadow-pink-200/30 p-3 text-center">
                    <span className="text-3xl md:text-4xl">{occasion.icon}</span>
                    <span className="text-[10px] md:text-xs font-semibold text-pink-600 mt-1">{occasion.label}</span>
                    <span className="text-[6px] md:text-[8px] text-pink-400 mt-0.5 hidden md:block truncate max-w-[80px]">{occasion.description.split(' ').slice(0, 4).join(' ')}...</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Category Grid - Soft & Cute */}
          <section className="w-full px-4 md:px-8 py-8 bg-white/70 backdrop-blur-sm">
            <div className="text-center mb-8">
              <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="categories_badge" 
                  value="Gift Categories"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="gifts"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-pink-700 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="categories_title" 
                  value="Explore Our Collections"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="gifts"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {giftCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("gifts", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("gifts", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 hover:border-pink-400">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-pink-700 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="gifts"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto rounded-full" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - No Add to Cart Button */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Best Sellers"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="gifts"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-pink-700 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Most Loved Gifts"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="gifts"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-pink-400 to-rose-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableGiftProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-pink-200 hover:border-pink-400">
                      <div className="relative h-48 overflow-hidden bg-pink-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            <span className="mr-1">✨</span>
                            {product.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-pink-500/80 text-white text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                          {product.quantity}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-500">{product.brand}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-pink-500">{product.category}</span>
                        </div>
                        <h3 className="font-semibold text-pink-700 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-pink-600">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-pink-50 text-pink-600 px-2 py-0.5 rounded-full border border-pink-200">
                            {product.subCategory}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                          <span>💝</span>
                          <span>{product.occasion || 'Perfect Gift'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => navigateTo("gifts", e)} 
                className="px-8 py-3 rounded-full bg-pink-500 text-white font-semibold hover:bg-pink-600 transition-all duration-300 hover:scale-105 shadow-lg shadow-pink-500/20"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Gifts"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="gifts"
                />
              </button>
            </div>
          </section>
          
          {/* Gift Stories - With Popup */}
          <section className="w-full px-4 md:px-8 py-8 bg-gradient-to-b from-pink-50 to-white">
            <div className="text-center mb-8">
              <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="stories_badge" 
                  value="Heartwarming Stories"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="stories"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-pink-700 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="stories_title" 
                  value="Gifts That Touch Hearts"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="stories"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableGiftStories.map((story, idx) => (
                <div 
                  key={story.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-pink-200 hover:border-pink-400 animate-card-fly"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={() => openStoryModal(story)}
                >
                  <div className="h-40 overflow-hidden relative">
                    <InlineEditableImage p={p} field={story.imageField} className="block h-full w-full overflow-hidden">
                      <img src={story.image} alt={story.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                    <div className="absolute inset-0 bg-gradient-to-t from-pink-900/60 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-4 text-white">
                      <h4 className="font-semibold text-sm">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={story.titleKey} 
                          value={story.title}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="stories"
                        />
                      </h4>
                      <p className="text-[10px] text-pink-200">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={story.descKey} 
                          value={story.desc}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="stories"
                        />
                      </p>
                    </div>
                    <div className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm text-pink-600 text-[10px] font-medium px-2 py-1 rounded-full">
                      Read Story
                    </div>
                  </div>
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">❤️ {Math.floor(Math.random() * 100 + 50)} loves</span>
                    <span className="text-pink-400 group-hover:translate-x-1 transition-transform text-sm">✨</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Newsletter - Dreamy */}
          <section className="bg-gradient-to-r from-pink-500 to-rose-500 py-16 mt-8 w-full">
            <div className="max-w-3xl mx-auto text-center px-4">
              <span className="text-pink-100 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="newsletter_badge" 
                  value="Stay Inspired"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="newsletter_title" 
                  value="Join Our Gift Lovers Community"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h3>
              <p className="text-pink-100 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="newsletter_subtitle" 
                  value="Get gift ideas, exclusive offers, and more ❤️"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input 
                  type="email" 
                  placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
                  className="flex-1 px-5 py-3 bg-white/20 border border-pink-300/30 rounded-full text-white placeholder-pink-200/60 focus:border-pink-300 outline-none transition" 
                />
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!(p.previewEditMode && p.activeEditField)) {
                      // Handle newsletter subscription
                    }
                  }}
                  className="px-6 py-3 rounded-full bg-white text-pink-600 font-semibold hover:bg-pink-50 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="subscribe_button" 
                    value="Subscribe ✨"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </button>
              </div>
            </div>
          </section>
        </>
      )}
      
      {/* ==================== GIFTS PAGE ==================== */}
      {activePage === "gifts" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="gifts_page_badge" 
                value="Gift Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="gifts"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-700 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="gifts_page_title" 
                value="Our Gift Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="gifts"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="gifts_page_subtitle" 
                value="Find the perfect gift for every occasion"
                onNavigate={handleNavigateAfterEdit}
                navPage="gifts"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-400 to-rose-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Gift Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {giftCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-pink-500 text-white shadow-lg" 
                    : "bg-white border border-pink-200 text-pink-600 hover:bg-pink-50"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {getFilteredItems(activeCategory).map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-pink-200 hover:border-pink-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-pink-50">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-pink-500/80 text-white text-[10px] font-bold px-3 py-1 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-pink-500">{product.category}</span>
                  </div>
                  <h3 className="font-semibold text-pink-700 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="gifts"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-pink-600">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="gifts"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.occasion || product.material}
                  </div>
                  <div className="mt-2 text-xs text-pink-400 flex items-center gap-1">
                    <span>💝</span>
                    <span>Perfect Gift</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== OCCASIONS PAGE ==================== */}
      {activePage === "occasions" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="occasions_page_badge" 
                value="Occasions"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-700 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="occasions_page_title" 
                value="Gifts for Every Occasion"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="occasions_page_subtitle" 
                value="Celebrate life's special moments"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-400 to-rose-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {occasionCategories.map((occasion, idx) => (
              <div 
                key={idx} 
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-pink-200 hover:border-pink-400 cursor-pointer"
              >
                <div className="text-center">
                  <div className="text-7xl mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                    {occasion.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-pink-700">{occasion.label}</h3>
                  <p className="text-gray-500 text-sm mt-3 leading-relaxed">
                    {occasion.description}
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <span className="text-xs bg-pink-50 text-pink-600 px-3 py-1 rounded-full border border-pink-200">
                      💝 Personalized Gifts
                    </span>
                    <span className="text-xs bg-pink-50 text-pink-600 px-3 py-1 rounded-full border border-pink-200">
                      🎁 Luxury Hampers
                    </span>
                    <span className="text-xs bg-pink-50 text-pink-600 px-3 py-1 rounded-full border border-pink-200">
                      🌹 Flowers
                    </span>
                  </div>
                  <button 
                    onClick={() => navigateTo("gifts")}
                    className="mt-4 px-6 py-2 rounded-full bg-pink-500 text-white text-sm font-semibold hover:bg-pink-600 transition"
                  >
                    Explore Gifts ✨
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== STORIES PAGE ==================== */}
      {activePage === "stories" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="stories_page_badge" 
                value="Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="stories"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-700 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="stories_page_title" 
                value="Heartwarming Gift Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="stories"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="stories_page_subtitle" 
                value="Real stories of love, joy, and giving"
                onNavigate={handleNavigateAfterEdit}
                navPage="stories"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-400 to-rose-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {editableGiftStories.map((story) => (
              <div 
                key={story.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-pink-200 hover:border-pink-400"
                onClick={() => openStoryModal(story)}
              >
                <div className="h-56 overflow-hidden relative">
                  <InlineEditableImage p={p} field={story.imageField} className="block h-full w-full overflow-hidden">
                    <img src={story.image} alt={story.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  <div className="absolute inset-0 bg-gradient-to-t from-pink-900/60 to-transparent pointer-events-none" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-xl font-bold">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={story.titleKey} 
                        value={story.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="stories"
                      />
                    </h3>
                    <p className="text-sm text-pink-200">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={story.descKey} 
                        value={story.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="stories"
                      />
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>❤️ {Math.floor(Math.random() * 200 + 100)} loves</span>
                    <span>📖 Read Story</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-pink-500 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About Enchanted Gifts"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-pink-700 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Gifts That Speak from the Heart"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-pink-500 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Enchanted Gifts is a curated collection of beautiful, personalized gifts for every occasion. We believe that every gift should tell a story and come from the heart."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="Our carefully selected collection includes personalized gifts, luxury hampers, flowers, and home decor. Each piece is chosen with love to bring joy to your loved ones."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-pink-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-pink-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="1000+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Gifts Delivered"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-pink-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="100%"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Satisfaction"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-pink-200/50">
              <InlineEditableImage p={p} field="enchantedAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.enchantedAboutImage || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=800&q=80"} 
                  alt="About Enchanted Gifts" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🎁 Shop"],
            ["services", "💝 Services"],
            ["details", "📖 About"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-pink-500 text-white shadow-lg" : "text-gray-500 hover:text-pink-600"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-pink-500 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  ✨ {selectedProduct.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-pink-500/80 text-white text-xs font-bold px-3 py-1 rounded-full">
                {selectedProduct.quantity}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">{selectedProduct.brand}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-pink-500">{selectedProduct.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-pink-700">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-pink-50 text-pink-600 px-2 py-1 rounded-full border border-pink-200">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.occasion && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {selectedProduct.occasion}
                  </span>
                )}
                {selectedProduct.material && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {selectedProduct.material}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-pink-600">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-pink-500 text-center bg-pink-50 p-3 rounded-full border border-pink-200">
                💝 A perfect gift for your loved one
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Story Modal */}
      {selectedStory && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-pink-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-pink-700">Heartwarming Story</h3>
              <button 
                onClick={closeStoryModal} 
                className="w-10 h-10 rounded-full bg-pink-50 text-pink-500 hover:bg-pink-500 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6 rounded-xl overflow-hidden">
                <InlineEditableImage 
                  p={p} 
                  field={selectedStory.imageField} 
                  className="block w-full overflow-hidden"
                >
                  <img 
                    src={selectedStory.image} 
                    alt={selectedStory.title} 
                    className="w-full h-72 object-cover" 
                  />
                </InlineEditableImage>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
                <span>✍️ 
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedStory.authorKey} 
                    value={selectedStory.author}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="stories"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>📅 
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedStory.dateKey} 
                    value={selectedStory.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="stories"
                    isInModal={true}
                  />
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-pink-700 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedStory.titleKey} 
                  value={selectedStory.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="stories"
                  isInModal={true}
                />
              </h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap text-base">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedStory.storyContentKey} 
                  value={selectedStory.fullContent}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="stories"
                  isInModal={true}
                />
              </p>
              <div className="mt-6 flex items-center gap-4 text-sm text-gray-400 border-t border-pink-100 pt-4">
                <span>❤️ {Math.floor(Math.random() * 300 + 150)} loves</span>
                <span>💬 {Math.floor(Math.random() * 50 + 10)} comments</span>
                <span>📤 Share this story</span>
              </div>
              <button 
                onClick={closeStoryModal} 
                className="mt-4 w-full py-3 rounded-full bg-pink-500 text-white font-semibold hover:bg-pink-600 transition"
              >
                Close Story ✨
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee-slow { animation: marquee-slow 25s linear infinite; }
        
        @keyframes heart-float { 
          0% { transform: translateY(0) scale(1) rotate(0deg); opacity: 0.05; }
          50% { transform: translateY(-40px) scale(1.2) rotate(10deg); opacity: 0.12; }
          100% { transform: translateY(-80px) scale(1) rotate(0deg); opacity: 0.05; }
        }
        .heart-float { animation: heart-float 7s ease-in-out infinite; }
        
        @keyframes star-float { 
          0% { transform: translateY(0) scale(1) rotate(0deg); opacity: 0.05; }
          50% { transform: translateY(-30px) scale(1.3) rotate(20deg); opacity: 0.1; }
          100% { transform: translateY(-60px) scale(1) rotate(0deg); opacity: 0.05; }
        }
        .star-float { animation: star-float 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9) rotate(-2deg); } 60% { transform: translateY(-5px) scale(1.02) rotate(1deg); } 100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}